/**
 * Node View Registry
 *
 * Implements a two-tier component lookup for ViewNode dialogs:
 *
 *   1. Mode-specific library — custom ViewNode per mode (e.g. ChatRoomViewNode)
 *   2. Common library        — fallback for modes not in modeRegistries, or for types not listed in a mode
 *
 * Lookup order for resolveViewNodeComponent(nodeType, mode):
 *   1. mode-specific registry[nodeType]   (exact match)
 *   2. commonRegistry[nodeType]           (exact match, any mode)
 *   3. mode-specific registry['text']     (mode default / catch-all)
 *   4. null                               (caller should handle gracefully)
 *
 * Adding a new type to all modes (e.g. 'poll'):
 *   → Add it to every entry in modeRegistries, then register it in
 *     commonRegistry as the default for any mode that doesn't override it.
 *
 * Adding a mode-specific override (e.g. a custom 'questionnaire' view for 'chat'):
 *   → Override the entry in modeRegistries['chat'] with the custom component.
 *
 * Usage:
 *   const ViewNode = resolveViewNodeComponent(getNodeType(nodeContent), 'chat');
 *   if (!ViewNode) return null;
 *   return <ViewNode open={open} onClose={handleClose} {...viewNodeProps} />;
 */

import { QuestionnaireViewNode } from './core/QuestionnaireViewNode';
import { ChatRoomViewNode } from './ForumChatRoom/ChatRoomViewNode';
import { StickerViewNode } from './ForumSticker/StickerViewNode';
import { WorkspaceViewNode } from './StickerWorkspace/WorkspaceViewNode';
import { TagForumViewNode } from './TagBasedForum/TagForumViewNode';

/**
 * Common library: types that serve as defaults for any mode not listed in
 * modeRegistries, or as fallback when a mode doesn't have an entry for a type.
 * Mode-specific entries always take precedence over commonRegistry.
 *
 * @type {Record<string, React.ComponentType>}
 */
const commonRegistry = {};

/**
 * Mode-specific libraries.
 * Each entry maps node type → component for a given mode.
 * The special key 'text' serves as the mode-level default / catch-all.
 *
 * All types that a mode handles should be listed explicitly here so
 * that each mode is self-documenting about the node types it supports.
 *
 * @type {Record<string, Record<string, React.ComponentType>>}
 */
const modeRegistries = {
  chat: {
    text: ChatRoomViewNode,
    questionnaire: QuestionnaireViewNode,
  },
  sticker: {
    text: StickerViewNode,
    questionnaire: QuestionnaireViewNode,
  },
  workspace: {
    text: WorkspaceViewNode,
    questionnaire: QuestionnaireViewNode,
  },
  'tagged-forum': {
    text: TagForumViewNode,
    questionnaire: QuestionnaireViewNode,
  },
};

/**
 * Resolves the ViewNode component for a node type within a given mode.
 *
 * @param {string} nodeType - e.g. 'text', 'questionnaire'
 * @param {string} mode     - e.g. 'chat', 'sticker', 'workspace', 'tagged-forum'
 * @returns {React.ComponentType|null}
 */
export function resolveViewNodeComponent(nodeType, mode) {
  const modeLib = modeRegistries[mode] || {};

  // 1. Mode-specific exact match
  if (modeLib[nodeType]) return modeLib[nodeType];

  // 2. Common exact match
  if (commonRegistry[nodeType]) return commonRegistry[nodeType];

  // 3. Mode-level default
  if (modeLib.text) return modeLib.text;

  return null;
}
